import { bodyElement, CLASS_NO_DISPLAY } from './consts';
import {
	awaitNextFrame,
	arrayize,
	setHeightFrom,
	awaitEventOn,
	freezeScroll,
	unfreezeScroll,
	getFocusablesFrom,
} from './functions';

// state variables
let initialized = false;
let isModalInDom = false;
let shouldRemain = false;
let isDescriptionTitleInFooter = false;
let onCloseCurrent = null;
let sourceContainer = null;
let currentAddedClass = null;
let previousFocus = null;
let alwaysTransferBack = false;

const CLASS_CLOSING = 'closing';

// elements to be initialized
let elementTitlesContainer;
let elementTopBar;
let elementContent;
let elementModal;
let elementBackground;
let elementSlot;
let elementTitle;
let elementTitlePicture;
let elementDescriptionPicture;
let elementCloseButton;

const elementMain = bodyElement.querySelector('main');
const { closeLabel } = bodyElement.dataset;

export const modalInit = () => {
	if (initialized) return;
	[
		elementModal,
		elementBackground,
		elementContent,
		elementSlot,
		elementTopBar,
		elementTitlesContainer,
		elementTitle,
		elementTitlePicture,
		elementDescriptionPicture,
		elementCloseButton,
	] = [
		{ classes: ['modal', CLASS_CLOSING] },
		{ classes: 'modal--background' },
		{ classes: 'modal--content' },
		{ classes: 'modal--slot' },
		{ classes: 'modal--top-bar' },
		{ classes: 'modal--titles' },
		{ classes: 'modal--title' },
		{ classes: 'modal--picture-title' },
		{ classes: 'modal--picture-description' },
		{ classes: 'modal--close-button', type: 'button' },
	].map(({ classes, type }) => {
		const element = document.createElement(type || 'div');
		arrayize(classes).forEach(className => element.classList.add(className));
		return element;
	});
	// a11y label on close button
	elementCloseButton.setAttribute('aria-label', closeLabel);
	elementSlot.setAttribute('tabindex', -1);
	// modal assembling
	elementTitlesContainer.append(
		elementTitle,
		!isDescriptionTitleInFooter ? elementTitlePicture : '',
	);
	elementTopBar.append(elementTitlesContainer, elementCloseButton);
	elementContent.append(elementTopBar, elementSlot);
	elementModal.append(elementBackground, elementContent);
	/* <div class="modal">
	<div class="modal--background"></div>
	<div class="modal--content">
	<div class="modal--top-bar">
	<div class="modal--slot">
	<div class="modal--titles">
	<h3 class="modal--title"></h3>
	<h4 class="modal--picture-title"></h4>
	</div>
	<button class="modal--close-button"></button>
	</div>
	<div class="modal--slot"></div>
	</div>
	</div>; */
	elementCloseButton.addEventListener('click', modalClose);
	elementBackground.addEventListener('click', modalClose);
	initialized = true;
};

// bring back the content in the element
const transferToElement = (container, target) =>
	target.append(
		...[...container.childNodes].filter(node => node.tagName !== 'SCRIPT'),
	);

// bring the modal in the dom
const appendModal = () => {
	if (!isModalInDom) {
		bodyElement.appendChild(elementModal);
		isModalInDom = true;
	} else elementModal.classList.remove(CLASS_NO_DISPLAY);
};
// remove modal from the dom
const removeModal = () => {
	if (isModalInDom) {
		bodyElement.removeChild(elementModal);
		isModalInDom = false;
	}
};
// transfer content from the source to the modal
const modalTransferContent = container => {
	elementSlot.innerHTML = null;
	sourceContainer = container;
	transferToElement(container, elementSlot);
	if (isDescriptionTitleInFooter) modalAddTitleDescription();
};
const modalAddTitleDescription = () => {
	elementSlot
		.querySelector('.modal--picture-info')
		.append(elementTitlePicture, elementDescriptionPicture);
};
const keyListener = ({ key }) => {
	key === 'Escape' && modalClose();
};
const outFocusListener = ({ target }) => {
	if (!elementContent.contains(target)) elementCloseButton.focus();
};
const toggleListeners = doAdd => {
	[
		['keyup', keyListener],
		['focusin', outFocusListener],
	].forEach(([eventName, callback]) =>
		document[doAdd ? 'addEventListener' : 'removeEventListener'](
			eventName,
			callback,
		),
	);
};

export const modalClose = async () => {
	// pre animation
	toggleListeners(false);
	unfreezeScroll();
	elementModal.classList.add(CLASS_CLOSING);
	setHeightFrom(elementContent, [elementTopBar, elementSlot]);
	elementContent.style.overflow = 'hidden';
	await awaitNextFrame();
	await awaitNextFrame();
	// animate to flat
	elementContent.style.height = 0;
	onCloseCurrent && onCloseCurrent();
	await awaitEventOn(elementBackground, 'transitionend');
	// remove
	if (shouldRemain) elementModal.classList.add(CLASS_NO_DISPLAY);
	else removeModal();
	// reinitialisation
	if (currentAddedClass) elementModal.classList.remove(currentAddedClass);
	modalSetTitle('');
	modalSetTitlePicture('');
	elementMain.removeAttribute('inert');
	// focus on previous focus
	previousFocus?.focus();
	if (alwaysTransferBack)
		sourceContainer && transferToElement(elementSlot, sourceContainer);
};
export const modalOpen = async () => {
	elementMain.setAttribute('inert', '');
	freezeScroll();
	toggleListeners(true);
	// append and init
	if (currentAddedClass) elementModal.classList.add(currentAddedClass);
	appendModal();
	await awaitNextFrame();
	// unfold
	setHeightFrom(elementContent, [elementTopBar, elementSlot]);
	elementModal.classList.remove(CLASS_CLOSING);
	// a11y read content
	elementSlot.focus();
	await awaitEventOn(elementContent, 'transitionend');
	// leave height to further layout events
	elementContent.style.height = 'auto';
	elementContent.style.overflow = 'visible';
};
export const modalSetTitle = title => {
	elementTitle.innerHTML = title != null ? title : '';
};
export const modalSetTitlePicture = title => {
	elementTitlePicture.innerHTML = title != null ? title : '';
};
export const modalSetDescriptionPicture = description => {
	elementDescriptionPicture.innerHTML = description != null ? description : '';
};
export const launchModal = async ({
	contentContainer,
	onClose,
	title,
	titlePicture,
	addedClass,
	remain,
	descriptionTitleInFooter,
	closeButtonContentClass,
	keep,
}) => {
	isDescriptionTitleInFooter = !!descriptionTitleInFooter;
	modalInit();
	onCloseCurrent = onClose;
	shouldRemain = !!remain;
	alwaysTransferBack = !!keep;

	if (closeButtonContentClass)
		elementCloseButton.classList.add('modal--close-button-in-content');
	if (addedClass) currentAddedClass = addedClass;

	if (sourceContainer !== contentContainer && !alwaysTransferBack) {
		sourceContainer && transferToElement(elementSlot, sourceContainer);
		contentContainer && modalTransferContent(contentContainer);
	} else if (alwaysTransferBack) {
		contentContainer && modalTransferContent(contentContainer);
	}
	previousFocus = document.activeElement || null;
	modalSetTitle(title);
	modalSetTitlePicture(titlePicture);
	await modalOpen();
};
